/**
 * Pages and the sections on them.
 *
 * The snapshot that seeds Page and PageSection, and the page the site renders
 * when the database has not been seeded. Every section has a stable `key`, and
 * the React route that renders it looks the section up by that key.
 *
 * The order of `sections` on each page is the order visitors meet them. The page
 * editor shows the same order, and a super admin can change it.
 *
 * That fallback is what makes this safe to put in front of staff. A super admin
 * can rewrite the homepage headline, swap the photograph, change the overlay,
 * reorder the trust points or hide a whole section, and the worst case is the
 * page renders the committed default instead of a blank space.
 *
 * Nothing here invents a fact. Every figure and date below already appears in
 * client/src/data with a source comment beside it, or on HISCUS's own website.
 * Copy that used to be written into a component was moved here word for word,
 * so the page reads exactly as it did.
 */

import { finalCtaFallback } from "./cta-options";
import { locations } from "./locations";
import { galleryPreview } from "./media";
import { pillars, type Pillar } from "./programs";
import { socialLinks } from "./site";

export type SeedBlock = {
  kind: "heading" | "paragraph" | "list" | "quote" | "image" | "video" | "callout" | "factList";
  text?: string;
  items?: string[];
  attribution?: string;
  mediaKey?: string;
  level?: number;
  tone?: "neutral" | "note" | "warning";
  facts?: { label: string; value: string }[];
};

export type SeedLink = { label: string; href: string; external?: boolean; description?: string; order?: number };

export type SeedItem = {
  title?: string;
  text?: string;
  value?: string;
  label?: string;
  eyebrow?: string;
  mediaKey?: string;
  href?: string;
  external?: boolean;
  icon?: string;
  date?: string;
  source?: string;
  focalPoint?: string;
  facts?: { label: string; value: string; date?: string }[];
  hidden?: boolean;
  order?: number;
};

export type SeedSection = {
  key: string;
  type: string;
  order: number;
  eyebrow?: string;
  title?: string;
  lead?: string;
  body?: SeedBlock[];
  mediaKey?: string;
  mediaKeys?: string[];
  videoUrl?: string;
  videoTitle?: string;
  videoUploadDate?: string;
  posterMediaKey?: string;
  items?: SeedItem[];
  facts?: { label: string; value: string }[];
  refSlug?: string;
  texts?: Record<string, string>;
  ctas?: SeedLink[];
  settings?: Partial<{
    tone: "white" | "mist" | "ivory" | "navy" | "midnight";
    columns: number;
    imageSide: "left" | "right";
    overlayStrength: number;
    focalPoint: string;
    align: "left" | "center";
    compact: boolean;
  }>;
  source?: string;
};

export type SeedPage = {
  slug: string;
  title: string;
  headline?: string;
  metaTitle?: string;
  metaDescription?: string;
  eyebrow?: string;
  lead?: string;
  schemaType?: "WebPage" | "AboutPage" | "ContactPage" | "CollectionPage";
  systemRoute: boolean;
  /** The public address. Code-owned, because the route table is. */
  routePath: string;
  /** The page this one sits under in the site's structure, for the panel's tree. */
  parent?: string;
  showInSitemap?: boolean;
  banner?: { ctas?: SeedLink[]; description?: string; mediaKey?: string; focalPoint?: string; showBreadcrumb?: boolean };
  order: number;
  sections: SeedSection[];
  source?: string;
};

/** Numbers a page's sections in the order they are listed. */
const inOrder = (sections: Omit<SeedSection, "order">[]): SeedSection[] =>
  sections.map((section, order) => ({ ...section, order }) as SeedSection);

/* ------------------------------------------------------------------ home --- */

/**
 * The homepage hero.
 *
 * The photograph changed from an aerial community site to a workplace group
 * session. The previous frame documented a real outcome, but the people were
 * tiny at homepage scale and most of the opening view read as empty hillside.
 * This frame puts the women doing the work at the centre of the first screen.
 *
 * It is also the right shape: a 2560px-wide landscape original. The group is
 * concentrated through the centre and right while the fibre on the left forms
 * a quieter ground under the headline.
 *
 * Every part of it is editable: image, focal point, overlay strength, eyebrow,
 * heading, paragraph, both buttons, and the line and link beneath them.
 */
const homeHero: Omit<SeedSection, "order"> = {
  key: "home.hero",
  type: "hero",
  eyebrow: "Community-led change in Nepal since 1998",
  title: "Building dignity, opportunity and resilience together.",
  lead: "Working alongside women, children and communities across Nepal.",
  mediaKey: "wg-workplace",
  settings: { overlayStrength: 30, focalPoint: "50% 45%" },
  texts: { credibility: "Registered nonprofit in Nepal · Established 1998 ·" },
  ctas: [
    { label: "Explore our work", href: "/our-work", order: 0 },
    { label: "Support our work", href: "/get-involved/support", order: 1 },
    { label: "Registration & transparency", href: "/transparency", order: 2 },
  ],
  source: "Photograph: HISCUS documentary archive. Exact programme attribution is awaiting confirmation.",
};

/**
 * "Why HISCUS", which replaces "Documented work in".
 *
 * The band it replaces scrolled seven place names past the reader and told them
 * nothing they could act on. This section answers the question a funder or a
 * partner actually arrives with: who is this organisation, and why would I work
 * with it rather than somebody else.
 *
 * Four trust points, each one checkable against something already published on
 * this site. The nominated HISCUS YouTube film appears beside the copy in a
 * privacy-enhanced, click-to-load player.
 */
const homeWhy: Omit<SeedSection, "order"> = {
  key: "home.why-hiscus",
  type: "videoText",
  eyebrow: "Who we are",
  title: "Why HISCUS",
  lead: "A Nepali organisation, not a country office. Registered in 1998, working with the same communities long enough to be held to what it said it would do.",
  body: [
    {
      kind: "paragraph",
      text: "HISCUS was founded and is run by Nepalis, in Nepal. Programmes start with a request from a ward, a school, a health post or a community group, and are planned with the people who will host them.",
    },
    {
      kind: "paragraph",
      text: "The organisation works in a small number of places repeatedly rather than in many places once. In Bethanchowk that meant a health post, a women's session and a water tank inside nine months, and the tank is still there.",
    },
  ],
  items: [
    {
      title: "Nepali-founded, Nepali-run",
      text: "Registered in Nepal in 1998, with the District Administration Office and the Social Welfare Council.",
      order: 0,
    },
    {
      title: "Locally led, and asked for",
      text: "Every programme begins with a request from the ward, school, health post or group that will host it.",
      order: 1,
    },
    {
      title: "Something durable left behind",
      text: "Relief work is expected to leave a tank, a path or a wall, not only a distribution day.",
      order: 2,
    },
    {
      title: "Evidence, with its period",
      text: "Every figure on this site carries the reporting period it refers to.",
      order: 3,
    },
  ],
  videoUrl: "https://www.youtube.com/watch?v=CnCrd5x9ob4",
  videoTitle: "Himshikhar Socio Cultural Society",
  videoUploadDate: "2017-11-22T05:02:20-08:00",
  posterMediaKey: "ct-welcome",
  ctas: [
    { label: "About HISCUS", href: "/about", order: 0 },
    { label: "Support our work", href: "/get-involved/support", order: 1 },
  ],
  settings: { tone: "white", columns: 2, imageSide: "right" },
  source:
    "Registration and founding year: https://himshikharanepal.org.np/homepage/about-us/. Bethanchowk sequence: client/src/data/projects.ts. Video supplied directly by HISCUS on 11 September 2026: https://www.youtube.com/watch?v=CnCrd5x9ob4.",
};

const homeEvidence: Omit<SeedSection, "order"> = {
  key: "home.evidence",
  type: "stats",
  eyebrow: "Nepali-founded, Nepali-run, since 1998",
  title: "A small number of places, worked in repeatedly, with something durable left behind each time.",
  ctas: [
    { label: "See the full impact record", href: "/impact", order: 0 },
    { label: "Where we work", href: "/where-we-work", order: 1 },
  ],
  source: "client/src/data/impact.ts",
};

const homeProgrammes: Omit<SeedSection, "order"> = {
  key: "home.programmes",
  type: "programmeCards",
  eyebrow: "What we do",
  title: "Four programmes",
  ctas: [{ label: "All programmes and projects", href: "/our-work", order: 0 }],
  source: "client/src/data/programs.ts",
};

/**
 * Where HISCUS works, on the homepage.
 *
 * This used to be an item in the main menu, which asked a visitor to leave the
 * homepage to find out where the organisation is. It sits here now, directly
 * beneath the four programmes, as the answer to "where" under "what". Each card
 * is one place named in HISCUS's own reporting, and links to what happened there.
 *
 * No photographs are attached. None of the documentary photographs has a
 * published location beyond Sarangkot, and putting a picture on the Bethanchowk
 * card would be a claim about where it was taken. An editor who has that
 * information can add one to any card.
 */
const homeWhereWeWork: Omit<SeedSection, "order"> = {
  key: "home.where-we-work",
  type: "locations",
  eyebrow: "Where we work",
  title: "Documented locations",
  lead: "HISCUS works in a small number of communities repeatedly rather than in many communities once. These are the places its own reporting names.",
  items: locations.map((place, order) => ({
    title: place.name,
    eyebrow: place.region,
    text: place.summary,
    label: place.pillars.join(" · "),
    href: `/where-we-work#${place.id}`,
    order,
  })),
  ctas: [{ label: "See what happened in each place", href: "/where-we-work", order: 0 }],
  settings: { tone: "ivory", columns: 3 },
  source: "client/src/data/locations.ts, transcribed from HISCUS project reporting.",
};

const homeGetInvolved: Omit<SeedSection, "order"> = {
  key: "home.get-involved",
  type: "linkCards",
  eyebrow: "Work with us",
  title: "Three ways to take part",
  lead: "Whichever route fits, it starts as a conversation with a person rather than a form that disappears.",
  items: [
    {
      title: "Support our work",
      eyebrow: "Foundations, INGOs, embassies, development agencies, companies and CSR teams.",
      text: "Legal identity, the delivery record, dated outcomes and the documents due diligence needs.",
      href: "/get-involved/support",
      label: "Explore support options",
      order: 0,
    },
    {
      title: "Volunteer or join Charity Trek",
      eyebrow: "Volunteers, students, travellers and service groups.",
      text: "Real roles on live programmes, honest costs, and Charity Trek run with Mountain Routes.",
      href: "/get-involved/volunteer",
      label: "See how volunteering works",
      order: 1,
    },
    {
      title: "Request local collaboration",
      eyebrow: "Schools, municipalities, wards, health posts, CBOs and community groups.",
      text: "How to request a session, a health camp or an infrastructure project in your community.",
      href: "/contact",
      label: "Propose local collaboration",
      order: 2,
    },
  ],
  source: "HISCUS public participation routes.",
};

const homeFeatured: Omit<SeedSection, "order"> = {
  key: "home.featured",
  type: "imageText",
  eyebrow: "Documented initiative · 2025",
  title: "Empowering with Dignity",
  mediaKey: "wg-dignity",
  /** The project whose dated facts are listed beside the photograph. */
  refSlug: "empowering-with-dignity",
  ctas: [
    {
      label: "Read the Empowering with Dignity project",
      href: "/projects/empowering-with-dignity",
      order: 0,
    },
  ],
  source: "client/src/data/projects.ts",
};

const homeStories: Omit<SeedSection, "order"> = {
  key: "home.stories",
  type: "featureCards",
  eyebrow: "From the field",
  title: "Recent reporting",
  ctas: [{ label: "All stories", href: "/stories", order: 0 }],
  source: "Published story collection.",
};

/**
 * Charity Trek, on the homepage.
 *
 * The button leaves the site, and the section says so before it does. Mountain
 * Routes runs the trekking half and operates the page the button opens; HISCUS
 * runs the service half. Presenting that page as though HISCUS operated it
 * would be the kind of implied relationship this project does not make.
 */
const homeTrek: Omit<SeedSection, "order"> = {
  key: "home.charity-trek",
  type: "cta",
  eyebrow: "Get involved",
  title: "Trek. Serve. Sustain.",
  lead: "Trekking in Nepal with Mountain Routes, combined with service days on the trails, in schools and at community sites.",
  items: [
    { title: "Trail cleanup", order: 0 },
    { title: "School maintenance", order: 1 },
    { title: "Classroom painting", order: 2 },
    { title: "Library development", order: 3 },
  ],
  mediaKeys: ["ct-trail", "ct-scarf"],
  ctas: [{ label: "How Charity Trek works with HISCUS", href: "/get-involved/charity-trek", order: 0 }],
  settings: { tone: "midnight" },
  source: "https://himshikharanepal.org.np/homepage/what-we-do/charity-trek-program/",
};

const homeGallery: Omit<SeedSection, "order"> = {
  key: "home.gallery",
  type: "photoGallery",
  eyebrow: "Gallery",
  title: "From programme days",
  mediaKeys: galleryPreview.slice(0, 4).map((item) => item.id),
  ctas: [{ label: "Open the full gallery", href: "/gallery", order: 0 }],
  source: "HISCUS documentary photograph archive.",
};

const homeClosing: Omit<SeedSection, "order"> = {
  key: "home.closing",
  type: "cta",
  title: "Support a programme, contribute your time, or deliver alongside us.",
  lead: "Every route in starts as a direct conversation with the HISCUS team.",
  body: [
    {
      kind: "paragraph",
      text: "Annual reports and audited financials are not published here yet. Supporters who need them for due diligence can request them directly.",
    },
  ],
  items: [{ title: "Accountability", label: "See registration and document status", href: "/transparency", order: 0 }],
  ctas: [
    { label: "Support our work", href: "/get-involved/support", order: 0 },
    { label: "See all the ways to take part", href: "/get-involved", order: 1 },
  ],
  source: "HISCUS registration settings and public document collection.",
};

/* ------------------------------------------------------- programme pages --- */

/**
 * A programme page's sections, built from its committed pillar.
 *
 * server/src/seed/programme-pages.ts builds the same shape from a stored
 * Programme record, so the migration can carry a live programme's edits across.
 * tests/programme-pages.test.ts keeps the two in step.
 */
export function programmePageSections(pillar: Pillar): Omit<SeedSection, "order">[] {
  const short = pillar.shortTitle || pillar.title;
  return [
    {
      key: `${pillar.slug}.introduction`,
      type: "programmeIntro",
      eyebrow: "Reported figures",
      title: "How this pillar is run",
      lead: pillar.intro,
      mediaKey: pillar.image.id,
      items: pillar.approach.map((title, order) => ({ title, order })),
      facts: pillar.evidence.map((row) => ({ label: row.label, value: row.value })),
      settings: { tone: "white" },
      source: pillar.source,
    },
    {
      key: `${pillar.slug}.activities`,
      type: "featureCards",
      eyebrow: "Activities",
      title: "What runs under this pillar",
      items: pillar.activities.map((row, order) => ({ title: row.title, text: row.description, order })),
      settings: { tone: "ivory", columns: 2 },
      source: pillar.source,
    },
    {
      key: `${pillar.slug}.gallery`,
      type: "photoGallery",
      eyebrow: "From this programme",
      title: "Photographs from the work",
      mediaKeys: pillar.gallery.map((item) => item.id),
      settings: { tone: "white" },
      source: pillar.source,
    },
    {
      key: `${pillar.slug}.projects`,
      type: "projectList",
      eyebrow: "Projects",
      title: `Projects in ${short}`,
      refSlug: pillar.slug,
      settings: { tone: "mist" },
      source: "The Projects collection, filtered to this programme.",
    },
    {
      key: `${pillar.slug}.stories`,
      type: "storyList",
      eyebrow: "Stories",
      title: "Reporting from this pillar",
      refSlug: pillar.slug,
      settings: { tone: "white" },
      source: "The Stories collection, filtered to this programme.",
    },
    {
      key: `${pillar.slug}.support`,
      type: "cta",
      title: `Support ${short}`,
      lead: "Partner on this pillar, fund a specific project, or propose local collaboration.",
      ctas: [
        { label: "Support our work", href: "/get-involved/support", order: 0 },
        { label: "Contact HISCUS", href: "/contact", order: 1 },
      ],
      settings: { tone: "navy" },
      source: "HISCUS public participation routes.",
    },
  ];
}

const programmePages: SeedPage[] = pillars.map((pillar, index) => ({
  slug: pillar.slug,
  title: pillar.title,
  headline: "",
  eyebrow: "Programme pillar",
  lead: "",
  metaDescription: "",
  schemaType: "WebPage",
  systemRoute: true,
  parent: "our-work",
  routePath: `/our-work/${pillar.slug}`,
  showInSitemap: true,
  order: 20 + index,
  source: pillar.source,
  sections: inOrder(programmePageSections(pillar)),
}));

/* ------------------------------------------------------------- the pages --- */

const youtube = socialLinks.find((link) => link.label === "YouTube");

export const seedPages: SeedPage[] = [
  {
    slug: "home",
    title: "Home",
    headline: "Building dignity, opportunity and resilience together.",
    metaTitle: "Himshikhara Socio-Cultural Society | Community-led change in Nepal since 1998",
    metaDescription:
      "HISCUS Nepal works alongside women, children and communities to expand learning, wellbeing, livelihoods and recovery. Registered nonprofit, established 1998.",
    systemRoute: true,
    routePath: "/",
    order: 0,
    sections: inOrder([
      homeHero,
      homeWhy,
      homeEvidence,
      homeProgrammes,
      homeWhereWeWork,
      homeGetInvolved,
      homeFeatured,
      homeStories,
      homeTrek,
      homeGallery,
      homeClosing,
    ]),
  },
  {
    slug: "about",
    title: "About HISCUS",
    headline: "A Nepali organisation, working in Nepali communities since 1998",
    schemaType: "AboutPage",
    eyebrow: "About",
    metaDescription:
      "HISCUS Nepal was established in 1998. Origin, mission, vision, values, programme areas, registration and governance.",
    systemRoute: true,
    routePath: "/about",
    banner: {
      ctas: [
        { label: "Support our work", href: "/get-involved/support", order: 0 },
        { label: "Contact HISCUS", href: "/contact", order: 1 },
      ],
    },
    order: 1,
    sections: inOrder([
      {
        key: "about.intro",
        type: "imageText",
        title: "A Nepali organisation, working in Nepali communities since 1998",
        body: [
          {
            kind: "paragraph",
            text: "Himshikhara Socio-Cultural Society, known as HISCUS Nepal, is a registered Nepali nonprofit established in 1998. It works with women and girls, children, and the communities around them, on programmes spanning health, education, livelihoods, infrastructure and public dialogue.",
          },
          {
            kind: "paragraph",
            text: "It works in a small number of places repeatedly rather than in many places once. That pattern is deliberate: it is what lets a community hold the organisation to what it said it would do.",
          },
        ],
        mediaKey: "vol-cohort",
        settings: { imageSide: "left" },
        source: "https://himshikharanepal.org.np/homepage/about-us/",
      },
      {
        key: "about.origin",
        type: "textBlock",
        eyebrow: "Origin",
        title: "How HISCUS started",
        body: [
          {
            kind: "paragraph",
            text: "HISCUS was founded in 1998 and registered with the District Administration Office and the Social Welfare Council. Its founding purpose, as the organisation states it, is preserving Nepal's cultural heritage and empowering communities to protect it.",
          },
          {
            kind: "paragraph",
            text: "The programme set grew outward from there: education support and a residential home for children, then women's health and livelihoods, then relief and the infrastructure that makes the next shock less damaging, and then the recorded public dialogue that sits across all of it.",
          },
        ],
        source: "https://himshikharanepal.org.np/homepage/about-us/",
      },
      {
        /*
         * Vision and mission. The words are the organisation's own statements
         * and live in the organisation profile, because the structured data
         * reads them from there too. This section decides where they appear.
         */
        key: "about.mission",
        type: "textBlock",
        settings: { tone: "navy" },
        source: "Organisation profile: vision and mission, from https://himshikharanepal.org.np/homepage/about-us/",
      },
      {
        key: "about.how-we-work",
        type: "featureCards",
        eyebrow: "How we work",
        title: "Four working principles",
        settings: { columns: 2, tone: "white" },
        source: "client/src/data/organisation.ts",
      },
      {
        key: "about.programmes",
        type: "programmeCards",
        eyebrow: "What we do",
        title: "Four programme areas",
        lead: "Each area links to its projects, activities and documented outcomes.",
        texts: { placesHeading: "Communities with dated work in HISCUS reporting" },
        ctas: [
          { label: "All programmes and projects", href: "/our-work", order: 0 },
          { label: "See what happened in each", href: "/where-we-work", order: 1 },
        ],
        source: "client/src/data/programs.ts",
      },
      {
        key: "about.timeline",
        type: "timeline",
        eyebrow: "Timeline",
        title: "Documented milestones",
        lead: "Only dated events HISCUS has published appear here.",
        items: [
          {
            date: "1998",
            title: "HISCUS is established",
            text: "Himshikhara Socio-Cultural Society is founded and registered in Nepal with the District Administration Office, and affiliated to the Social Welfare Council.",
            order: 0,
          },
          {
            date: "2018",
            title: "The Sarangkot Nestling Home is completed",
            text: "Completed on 13 April 2018, in partnership with The Nestling Trust UK, and running continuously since.",
            order: 1,
          },
          {
            date: "2024",
            title: "Thapathali flood response",
            text: "On 8–9 October, clothing support for affected children and a community water-storage tank. A roundtable on gendered violence is convened the same year.",
            order: 2,
          },
          {
            date: "2025",
            title: "A year of sessions, supplies and water",
            text: "Bethanchowk health post support and an Empowering with Dignity session in January; a Bhaktapur psychosocial session in March; 25 participants at Ropeway Sadak in June; and the Bethanchowk water tank completed in July.",
            order: 3,
          },
        ],
        mediaKeys: ["rr-infrastructure", "ct-welcome", "cd-dialogue"],
        source: "HISCUS project and programme reporting represented in client/src/data/projects.ts.",
      },
      {
        key: "about.evidence",
        type: "stats",
        eyebrow: "Evidence",
        title: "What the record shows",
        lead: "Each figure carries the reporting period it refers to.",
        ctas: [{ label: "The full impact record", href: "/impact", order: 0 }],
        source: "client/src/data/impact.ts",
      },
      {
        key: "about.registration",
        type: "textBlock",
        eyebrow: "Legal identity",
        title: "Registration and affiliation",
        ctas: [{ label: "See the transparency page", href: "/transparency", order: 0 }],
        source: "https://himshikharanepal.org.np/homepage/about-us/",
      },
      {
        key: "about.governance",
        type: "featureCards",
        eyebrow: "Organisation and governance",
        title: "A Nepali nonprofit working through partnerships",
        lead:
          "HISCUS combines its registered Nepali identity with cooperation across communities, public bodies and civil-society partners.",
        items: [
          {
            title: "Registered in Nepal",
            label: "Legal identity",
            text: "Established in 1998 and registered with the District Administration Office, with Social Welfare Council affiliation and a PAN number.",
            order: 0,
          },
          {
            title: "Partnership-based delivery",
            label: "How programmes are delivered",
            text: "HISCUS states that it works with government agencies, NGOs, community-based organisations, the private sector and individual donors.",
            order: 1,
          },
          {
            title: "Community participation",
            label: "Who shapes the work",
            text: "Its published approach engages youth, women and local communities in practical programmes and community development.",
            order: 2,
          },
          {
            title: "Four current focus areas",
            label: "Programme direction",
            text: "The official About page names women's empowerment, education, community relief and child support as HISCUS's current thematic focus.",
            order: 3,
          },
        ],
        settings: { columns: 2, tone: "white" },
        source: "https://himshikharanepal.org.np/homepage/about-us/ (reviewed 11 September 2026).",
      },
      {
        key: "about.accountability",
        type: "textBlock",
        eyebrow: "Accountability",
        title: "What HISCUS publishes",
        body: [
          {
            kind: "paragraph",
            text: "Figures on this site carry the reporting period they refer to. Where a detail has not been published by the organisation, it is left out rather than estimated.",
          },
          {
            kind: "paragraph",
            text: "Annual reports and audited financials will appear here when HISCUS clears them for public release. Funders may request available due-diligence documents directly.",
          },
        ],
        ctas: [{ label: "Request documentation", href: "/get-involved/support", order: 0 }],
        source: "HISCUS public document collection and publication rules.",
      },
      {
        key: "about.closing",
        type: "cta",
        title: "Work with a Nepali organisation that publishes what it can evidence.",
        ctas: [
          { label: "Support our work", href: "/get-involved/support", order: 0 },
          { label: "Contact HISCUS", href: "/contact", order: 1 },
        ],
        settings: { tone: "midnight" },
        source: "HISCUS public participation routes.",
      },
    ]),
  },
  {
    slug: "get-involved",
    title: "Get involved",
    headline: "Four ways to work alongside HISCUS",
    eyebrow: "Get involved",
    lead: "Whichever route you take, it starts as a conversation with a person rather than a form that disappears.",
    metaDescription:
      "Four ways to work with HISCUS Nepal: support our work, volunteer with us, join a Charity Trek, or propose local collaboration.",
    systemRoute: true,
    routePath: "/get-involved",
    order: 2,
    sections: inOrder([
      {
        key: "get-involved.pathways",
        type: "linkCards",
        items: [
          {
            title: "Support our work",
            eyebrow: "Foundations, INGOs, embassies, development agencies, universities and companies",
            text: "Co-design or fund a programme in any of the four pillars, or commission delivery in a community where HISCUS already works.",
            href: "/get-involved/support",
            label: "Start a support conversation",
            order: 0,
          },
          {
            title: "Volunteer",
            eyebrow: "Nepal-based and international volunteers and students",
            text: "Contribute to live programme work: session preparation, making sessions, supplies, documentation and school or infrastructure days.",
            href: "/get-involved/volunteer",
            label: "See how volunteering works",
            order: 1,
          },
          {
            title: "Charity Trek",
            eyebrow: "Ethical travellers and groups",
            text: "Trek in Nepal with Mountain Routes and take part in trail cleanup, school maintenance, classroom painting and library development.",
            href: "/get-involved/charity-trek",
            label: "Explore Charity Trek",
            order: 2,
          },
          {
            title: "Work with us locally",
            eyebrow: "Schools, wards, health posts, CBOs and community groups",
            text: "Propose a session, a health camp or an infrastructure project in your community, or invite HISCUS to an existing programme.",
            href: "/contact",
            label: "Propose local collaboration",
            order: 3,
          },
        ],
        settings: { columns: 2, tone: "white" },
        source: "HISCUS public participation routes.",
      },
      {
        key: "get-involved.support",
        type: "imageText",
        eyebrow: "Support our work",
        title: "Funding a programme, honestly explained",
        body: [
          {
            kind: "paragraph",
            text: "HISCUS does not yet publish a verified online donation route on this site. Support enquiries open a direct conversation about what you would like to fund and how the transfer would work.",
          },
          {
            kind: "paragraph",
            text: "Support goes into programme delivery: session materials, learning support, health supplies, and community infrastructure such as water tanks, access paths and protective walls.",
          },
        ],
        items: [
          {
            title: "A community water tank",
            text: "Bethanchowk, October 2024 to July 2025. Restored reliable water access for at least 60 households.",
            order: 0,
          },
          {
            title: "An Empowering with Dignity session",
            text: "Ropeway Sadak, 13 June 2025. Twenty-five participants, each leaving with a reusable pad they made and the method to make more.",
            order: 1,
          },
          {
            title: "A year at the Sarangkot Nestling Home",
            text: "Eleven girls supported as reported for 2025, in a home running continuously since April 2018.",
            order: 2,
          },
          {
            title: "Community access infrastructure",
            text: "A 700-metre shortcut path in Sarangkot and a protective wall for the Bhaktapur Old Age Home.",
            order: 3,
          },
        ],
        texts: {
          panelHeading: "What support pays for",
          panelNote:
            "Real units of work from the documented record. Costs are confirmed with you directly. None is published here, because none has been published by HISCUS.",
        },
        mediaKey: "vol-planning",
        ctas: [{ label: "Talk to us about funding", href: "/get-involved/support", order: 0 }],
        source: "HISCUS documented programme record.",
      },
      {
        key: "get-involved.enquiry",
        type: "textBlock",
        eyebrow: "Get in touch",
        title: "Tell us which route interests you",
        lead: "One form, routed by the interest you select. We reply to the email address you give.",
        mediaKey: "ct-scarf",
        texts: { formHeading: "Send an enquiry" },
        source: "HISCUS enquiry form configuration.",
      },
      {
        key: "get-involved.faq",
        type: "faq",
        eyebrow: "Common questions",
        title: "What people ask before getting in touch",
        source: "HISCUS frequently asked questions collection.",
      },
      {
        key: "get-involved.summary",
        type: "cta",
        eyebrow: "In summary",
        title: "Pick the route that fits",
        items: finalCtaFallback.map((option, order) => ({
          title: option.title,
          text: option.description,
          href: option.href,
          label: option.action,
          order,
        })),
        settings: { tone: "midnight" },
        source: "HISCUS public participation routes.",
      },
    ]),
  },
  {
    slug: "support",
    title: "Support our work",
    headline: "Back work that communities have asked for",
    eyebrow: "Support our work",
    lead: "HISCUS works with funders and institutions that need programmes delivered well in Nepali communities, and reported honestly afterwards. Individual supporters fund the same delivery.",
    metaDescription:
      "Fund, partner with or sponsor HISCUS Nepal: institutional partnership, CSR, programme sponsorship, volunteering and Charity Trek.",
    systemRoute: true,
    routePath: "/get-involved/support",
    parent: "get-involved",
    order: 3,
    sections: inOrder([
      {
        key: "support.audiences",
        type: "featureCards",
        eyebrow: "Who this page is for",
        title: "Ways to work with HISCUS",
        items: [
          {
            title: "Institutional partnership",
            text: "Foundations, INGOs, embassies and development agencies co-designing or funding a programme in one of the four pillars.",
            order: 0,
          },
          {
            title: "CSR partnership",
            text: "Companies and CSR teams funding delivery in a community where HISCUS already works, with reporting against what was delivered.",
            order: 1,
          },
          {
            title: "Programme sponsorship",
            text: "Sponsoring a defined unit of work: a session, a year at the Nestling Home, a water tank, an access path.",
            order: 2,
          },
          {
            title: "Individual and diaspora support",
            text: "Individual supporters and Nepali diaspora giving directly to programme delivery. Transfers are arranged in conversation.",
            order: 3,
          },
          {
            title: "Volunteer route",
            text: "Contribute skills and time to live programme work rather than to a role invented for a visitor.",
            href: "/get-involved/volunteer",
            order: 4,
          },
          {
            title: "Charity Trek route",
            text: "Trek with Mountain Routes and take part in service days at the schools, trails and community sites HISCUS supports.",
            href: "/get-involved/charity-trek",
            order: 5,
          },
        ],
        settings: { columns: 3, tone: "white" },
        source: "client/src/data/audiences.ts and the documented programme record.",
      },
      {
        key: "support.funds",
        type: "featureCards",
        eyebrow: "What support funds",
        title: "Units of work, with the real figures attached",
        lead: "Costs are confirmed with you directly. None is published here, because none has been published by HISCUS.",
        items: [
          {
            title: "A community water tank",
            text: "Bethanchowk, October 2024 to July 2025. Restored reliable water access for at least 60 households.",
            order: 0,
          },
          {
            title: "An Empowering with Dignity session",
            text: "Ropeway Sadak, 13 June 2025. Twenty-five participants, each leaving with a reusable pad they made and the method to make more.",
            order: 1,
          },
          {
            title: "A year at the Sarangkot Nestling Home",
            text: "Eleven girls supported as reported for 2025, in a home running continuously since April 2018.",
            order: 2,
          },
          {
            title: "Community access infrastructure",
            text: "A 700-metre shortcut path in Sarangkot and a protective wall for the Bhaktapur Old Age Home.",
            order: 3,
          },
        ],
        source: "HISCUS dated project and impact records.",
      },
      {
        key: "support.capability",
        type: "featureCards",
        eyebrow: "What HISCUS brings",
        title: "A local delivery partner with a documented record",
        items: [
          {
            title: "Registered since 1998",
            text: "A Nepali nonprofit with CDO registration, Social Welfare Council affiliation and a PAN number.",
            order: 0,
          },
          {
            title: "Community relationships",
            text: "Existing work with wards, schools, health posts and community groups in the places where projects run.",
            order: 1,
          },
          {
            title: "Dated reporting",
            text: "A delivery record with dates and places attached, rather than aggregate figures without a period.",
            order: 2,
          },
          {
            title: "Long-term delivery",
            text: "Experience running a residential home continuously since 2018 in partnership with a UK trust.",
            order: 3,
          },
        ],
        mediaKey: "vol-cohort",
        source: "HISCUS official About page and documented programme record.",
      },
      {
        key: "support.partners",
        type: "imageText",
        eyebrow: "Working with others",
        title: "Who HISCUS already works with",
        mediaKey: "rr-infrastructure",
        source: "HISCUS public partner collection.",
      },
      {
        key: "support.due-diligence",
        type: "textBlock",
        eyebrow: "Due diligence",
        title: "What we can send you",
        body: [
          {
            kind: "paragraph",
            text: "Registration references are published on this site. Reports, audited financials and approved policies appear in the document library when HISCUS clears them for public release.",
          },
          {
            kind: "paragraph",
            text: "If your due-diligence process needs other governance documents, tell HISCUS what is required and the team will confirm what can be supplied and when.",
          },
          {
            kind: "callout",
            tone: "note",
            text: "Documents appear publicly only after HISCUS has cleared them for release.",
          },
        ],
        texts: { formHeading: "Start a support conversation" },
        ctas: [{ label: "Registration and document status", href: "/transparency", order: 0 }],
        source: "HISCUS public document collection and publication rules.",
      },
    ]),
  },
  {
    slug: "volunteer",
    title: "Volunteer with us",
    headline: "Bring useful skills to work already under way",
    eyebrow: "Volunteer",
    lead: "Volunteers with HISCUS cut fabric, carry supplies, prepare sessions and write them up. It is ordinary, useful work, and that is the point.",
    metaDescription:
      "Volunteer on live HISCUS programme work in Nepal: session preparation, pad making, supplies, documentation and school days.",
    systemRoute: true,
    routePath: "/get-involved/volunteer",
    parent: "get-involved",
    banner: {
      /*
       * The enquiry type travels in the URL rather than in state, so the link
       * works from anywhere: a menu, an email, a message somebody forwarded.
       */
      ctas: [
        { label: "Volunteer with us", href: "/contact?interest=volunteer", order: 0 },
        { label: "Ask a question first", href: "#volunteer-enquiry", order: 1 },
      ],
    },
    order: 4,
    sections: inOrder([
      {
        key: "volunteer.intro",
        type: "imageText",
        title: "How volunteering works here",
        body: [
          {
            kind: "paragraph",
            text: "Volunteers join programmes that are already running, in communities that have already asked for them. You will be given specific tasks rather than a role invented for a visitor.",
          },
          {
            kind: "paragraph",
            text: "Placements are agreed individually: what you will do, for how long, where, and what it costs. Nothing is confirmed until it is confirmed in writing.",
          },
        ],
        mediaKey: "vol-cohort",
        source: "HISCUS volunteer programme record.",
      },
      {
        key: "volunteer.roles",
        type: "featureCards",
        eyebrow: "Roles",
        title: "What volunteers do",
        lead: "Drawn from the work volunteers have actually taken part in.",
        items: [
          {
            title: "Session support",
            text: "Preparing materials, setting up venues and helping run menstrual health, wellbeing and livelihood sessions.",
            mediaKey: "wg-pad-making",
            order: 0,
          },
          {
            title: "Making and production",
            text: "Cutting, stitching and finishing reusable pads, and supporting practical vocational sessions.",
            mediaKey: "vol-stitching",
            order: 1,
          },
          {
            title: "Supplies and programme days",
            text: "Packing, carrying and handing over materials for health posts and community responses.",
            mediaKey: "vol-group",
            order: 2,
          },
          {
            title: "Documentation and planning",
            text: "Writing up sessions, keeping programme records and preparing material for reporting.",
            mediaKey: "vol-planning",
            order: 3,
          },
        ],
        settings: { columns: 2, tone: "ivory" },
        source: "HISCUS volunteer photograph and programme archive.",
      },
      {
        key: "volunteer.practicalities",
        type: "faq",
        eyebrow: "Practicalities",
        title: "The questions worth asking first",
        items: [
          {
            title: "What time commitment is expected?",
            text: "Time commitment is agreed case by case, based on the programme you join and how long you are in Nepal. Tell us your dates and HISCUS will say what is realistic.",
            order: 0,
          },
          {
            title: "Who can volunteer?",
            text: "Both Nepal-based and international volunteers take part, including students. Eligibility for a specific role depends on the programme and is confirmed before you commit.",
            order: 1,
          },
          {
            title: "Are there costs?",
            text: "Costs, contributions and what they cover are confirmed in writing before a placement is agreed. HISCUS does not publish a fee schedule that has not been set.",
            order: 2,
          },
          {
            title: "What about safeguarding?",
            text: "Roles involving children carry additional requirements, set out in writing before a placement is agreed. Image and consent rules are described on the safeguarding page.",
            order: 3,
          },
        ],
        texts: { formHeading: "Enquire about volunteering" },
        source: "HISCUS volunteer enquiry route and safeguarding policy.",
      },
    ]),
  },
  {
    slug: "charity-trek",
    title: "Charity Trek",
    headline: "Walk the route. Work alongside the community.",
    eyebrow: "Charity Trek",
    lead: "Run in partnership with Mountain Routes: a trekking route through Nepal, combined with service days that feed the schools, trails and community sites HISCUS already supports.",
    metaDescription:
      "Charity Trek: trek in Nepal with Mountain Routes and take part in service days at the schools, trails and community sites HISCUS supports.",
    systemRoute: true,
    routePath: "/get-involved/charity-trek",
    parent: "get-involved",
    order: 5,
    sections: inOrder([
      {
        key: "charity-trek.explainer",
        type: "textBlock",
        eyebrow: "What it is",
        title: "Trekking and community support, run together",
        mediaKey: "ct-trail",
        body: [
          {
            kind: "paragraph",
            text: "Charity Trek pairs a trekking route in Nepal with service days in the communities the route passes through. Mountain Routes handles the trekking half: logistics, guiding and safety. HISCUS handles the service half, and directs it into places it already supports so the work is maintained after the group leaves.",
          },
          {
            kind: "list",
            items: [
              "Who can take part: individuals and groups travelling in Nepal, arranged per departure with Mountain Routes.",
              "What the contribution involves: practical work on trails, schools and community sites, plus the cost of the trek itself.",
              "What is confirmed per trek: dates, duration, eligibility, costs, inclusions, safety arrangements and how the contribution is allocated.",
            ],
          },
        ],
        items: [
          {
            title: "1. Trek",
            text: "Walk a route in Nepal with Mountain Routes, the trekking partner handling logistics, guiding and safety.",
            order: 0,
          },
          {
            title: "2. Serve",
            text: "Take part in service days: trail cleanup, school maintenance, classroom painting, infrastructure support and library development.",
            order: 1,
          },
          {
            title: "3. Sustain",
            text: "The work goes into places HISCUS already supports, so it can be maintained after the group leaves.",
            order: 2,
          },
        ],
        source: "https://himshikharanepal.org.np/homepage/what-we-do/charity-trek-program/",
      },
      {
        key: "charity-trek.activities",
        type: "featureCards",
        eyebrow: "The service half",
        title: "What participants actually do",
        lead: "These are the activities HISCUS lists for the programme.",
        items: [
          {
            title: "Trail cleanup",
            text: "Clearing waste from trekking routes, alongside the waste-management work HISCUS runs at ward level.",
            order: 0,
          },
          {
            title: "School cleaning and maintenance",
            text: "Practical repair and upkeep work at schools in the communities the route passes through.",
            order: 1,
          },
          {
            title: "Classroom painting",
            text: "Repainting and refurbishing classrooms, usually across a full service day.",
            order: 2,
          },
          {
            title: "Infrastructure support",
            text: "Contributing to paths, walls and community structures HISCUS builds and maintains.",
            order: 3,
          },
          {
            title: "Library development",
            text: "Building and stocking library spaces to support before- and after-school learning.",
            order: 4,
          },
        ],
        settings: { columns: 3, tone: "ivory" },
        source: "https://himshikharanepal.org.np/homepage/what-we-do/charity-trek-program/",
      },
      {
        key: "charity-trek.gallery",
        type: "photoGallery",
        eyebrow: "On the ground",
        title: "From programme days",
        mediaKeys: ["ct-welcome", "ct-scarf", "cd-cultivation", "gal-travel"],
        source: "HISCUS documentary photograph archive.",
      },
      {
        key: "charity-trek.enquiry",
        type: "textBlock",
        eyebrow: "Plan a trek",
        title: "Dates, costs and what is included",
        body: [
          {
            kind: "paragraph",
            text: "Departure dates, duration, eligibility, costs, inclusions, safety arrangements, how the contribution is allocated and cancellation terms are confirmed with Mountain Routes and HISCUS for each group.",
          },
          {
            kind: "paragraph",
            text: "Send your preferred dates and group size to receive the actual terms in writing.",
          },
          {
            kind: "callout",
            tone: "note",
            text: "Mountain Routes handles trekking logistics, guiding and safety. HISCUS coordinates the community-service work.",
          },
        ],
        texts: { formHeading: "Enquire about a Charity Trek" },
        settings: { tone: "mist" },
        source: "HISCUS and Mountain Routes Charity Trek delivery roles.",
      },
    ]),
  },
  {
    slug: "contact",
    title: "Contact",
    headline: "Start a conversation with HISCUS",
    schemaType: "ContactPage",
    eyebrow: "Contact",
    lead: "One form for every kind of enquiry. Choose what it is about and it reaches the right person.",
    metaDescription:
      "Contact Himshikhara Socio-Cultural Society (HISCUS Nepal) about support, funding, CSR, volunteering, Charity Trek, local collaboration, or media and research.",
    systemRoute: true,
    routePath: "/contact",
    order: 6,
    sections: inOrder([
      {
        /*
         * The address, phone, email and social channels come from the
         * organisation profile and appear only once they are confirmed there.
         */
        key: "contact.details",
        type: "contactForm",
        eyebrow: "Details",
        title: "Where to find us",
        texts: {
          noEmail:
            "Use the enquiry form on this page, or reach HISCUS through the social channels below. Both are read by the team.",
          formHeading: "Send us a message",
        },
        source: "Organisation profile contact details and the enquiry form configuration.",
      },
    ]),
  },
  {
    slug: "gallery",
    title: "Gallery",
    headline: "Gallery",
    schemaType: "CollectionPage",
    eyebrow: "Photographs and video",
    lead: "Filter by type, programme, place or year. Captions describe what is in the frame; project, place and date are shown wherever HISCUS has published them.",
    metaDescription:
      "Photographs and video from HISCUS programme days in Nepal: women's sessions, education, relief work, dialogue and Charity Trek.",
    systemRoute: true,
    routePath: "/gallery",
    order: 7,
    sections: inOrder([
      {
        key: "gallery.browser",
        type: "photoGallery",
        texts: {
          noteTitle: "About these photographs",
          noteBody:
            "Documentary photographs from HISCUS programmes, published as they were taken. Where a frame shows an identifiable child, it appears only once consent for public use has been recorded. Captions name the project, place and date wherever HISCUS has published them.",
        },
        source: "The gallery collection.",
      },
    ]),
  },
  {
    slug: "impact",
    title: "Impact",
    headline: "What HISCUS can evidence",
    eyebrow: "Evidence",
    lead:
      "Every figure on this page carries the period it refers to. Where a reporting period is not yet confirmed, that is stated instead of being rounded away.",
    metaDescription:
      "Dated and sourced evidence from HISCUS programmes, with reporting periods and unresolved figures clearly identified.",
    systemRoute: true,
    routePath: "/impact",
    order: 8,
    sections: inOrder([
      {
        key: "impact.headline",
        type: "stats",
        eyebrow: "Headline figures",
        title: "Three numbers, each with its source",
        settings: { tone: "navy" },
        source: "The impact figures collection.",
      },
      {
        key: "impact.record",
        type: "stats",
        eyebrow: "The record",
        title: "Documented outcomes by programme and year",
        lead: "Filter the dated outcomes HISCUS has published. Each entry names its place and date.",
        source: "client/src/data/impact.ts",
      },
      {
        key: "impact.method",
        type: "textBlock",
        eyebrow: "How we report",
        title: "What is on this page, and what is not",
        body: [
          {
            kind: "paragraph",
            text: "Only figures HISCUS has published, with the period they refer to, appear as headline metrics. Social media follower counts are not treated as impact.",
          },
          {
            kind: "paragraph",
            text: "Several older figures circulate in HISCUS material without a clear reporting period. They are listed here for completeness, but they are deliberately kept out of the counters until their date, scope and current validity are confirmed:",
          },
        ],
        /** Figures HISCUS has published without a clear reporting period. Shown, never as counters. */
        items: [
          { title: "420+ health camp participants", order: 0 },
          { title: "175 women in computer education", order: 1 },
          { title: "18 CCID learners", order: 2 },
          { title: "200+ Nirantar Shiksha participants", order: 3 },
        ],
        texts: {
          panelTitle: "Reports and financials",
          panelBody:
            "Annual reports, audited financials and policy documents are not published on this site yet. They will be added to the Transparency page once HISCUS confirms which documents may be released publicly.",
          panelNote:
            "Funders and partners who need governance documentation before a decision can request it directly.",
        },
        ctas: [
          { label: "See registration details", href: "/transparency", order: 0 },
          { label: "Request documentation", href: "/get-involved/support", order: 1 },
        ],
        settings: { tone: "ivory" },
        source: "CONTENT_CONFIRMATION.md: legacy figures without a reporting period.",
      },
    ]),
  },
  {
    slug: "media",
    title: "Media",
    headline: "Media, podcasts and public dialogue",
    eyebrow: "Insights & Dialogue",
    lead: "HISCUS records the conversations its programmes raise, so they outlive the room they happened in.",
    metaDescription: "Media, podcasts and public dialogue from HISCUS Nepal.",
    systemRoute: true,
    routePath: "/media",
    order: 9,
    sections: inOrder([
      {
        key: "media.watch",
        type: "imageText",
        title: "Watch and listen",
        mediaKey: "cd-dialogue",
        body: [
          {
            kind: "paragraph",
            text: "Episodes of the Insights & Dialogue series, along with documentaries and recorded roundtables, are published on the HISCUS YouTube channel.",
          },
          {
            kind: "paragraph",
            text: "Recordings are embedded here as click-to-play only. Nothing autoplays, and no video loads until a visitor asks for it.",
          },
        ],
        texts: {
          noteTitle: "Captions and transcripts",
          noteBody:
            "Captions and transcripts will be published alongside each recording as HISCUS supplies them. Until then, this page links to the source channel rather than embedding media without an accessible alternative.",
        },
        ctas: [
          ...(youtube ? [{ label: "Open the HISCUS YouTube channel", href: youtube.href, external: true, order: 0 }] : []),
          {
            label: "Explore Culture, Environment & Dialogue",
            href: "/our-work/culture-environment-dialogue",
            order: 1,
          },
        ],
        source: "HISCUS YouTube channel and the Culture, Environment & Dialogue programme.",
      },
      {
        key: "media.subjects",
        type: "featureCards",
        eyebrow: "Subjects covered",
        title: "What the series talks about",
        lead: "These are the subjects HISCUS states the series covers. Individual episode titles and dates will be listed here once supplied.",
        items: [
          {
            title: "Waste and the ward",
            text: "Waste management, composting and the Ward 3 waste-management discussion held in 2025.",
            order: 0,
          },
          {
            title: "Mental health",
            text: "Conversations connected to the psychosocial wellbeing and stress-management sessions.",
            order: 1,
          },
          {
            title: "Feminism and gendered violence",
            text: "Including the academic roundtable on gendered violence convened in 2024.",
            order: 2,
          },
          {
            title: "Menstrual dignity",
            text: "Recorded discussion linked to the Empowering with Dignity programme.",
            order: 3,
          },
          {
            title: "Women's resilience",
            text: "Conversations with and about the women taking part in HISCUS programmes.",
            order: 4,
          },
        ],
        settings: { tone: "ivory", columns: 3 },
        source: "Subjects HISCUS has stated the series covers. No episode titles, dates or transcripts are asserted.",
      },
      {
        key: "media.press",
        type: "textBlock",
        eyebrow: "For media and researchers",
        title: "Using HISCUS material",
        body: [
          {
            kind: "paragraph",
            text: "Journalists, researchers and students are welcome to use HISCUS material with attribution. Photographs remain the property of HISCUS and its photographers.",
          },
          {
            kind: "paragraph",
            text: "Requests for higher-resolution images, interviews or programme data go through the contact form. Select Media or research as the enquiry type.",
          },
        ],
        texts: { followTitle: "Follow HISCUS" },
        ctas: [{ label: "Make a media or research request", href: "/contact", order: 0 }],
        settings: { tone: "mist" },
        source: "HISCUS media and research enquiry route.",
      },
    ]),
  },
  {
    slug: "news",
    title: "News",
    headline: "Announcements and reporting",
    eyebrow: "News",
    lead:
      "Everything HISCUS has published here, newest first. Each piece carries the date it refers to and, where one exists, the source it came from.",
    metaDescription: "Announcements, updates and dated programme reporting from HISCUS Nepal.",
    systemRoute: true,
    routePath: "/news",
    order: 10,
    sections: inOrder([{ key: "news.list", type: "featureCards", source: "The articles collection." }]),
  },
  {
    slug: "our-work",
    title: "Our work",
    headline: "Four pillars, and the projects underneath them",
    eyebrow: "Programmes",
    lead:
      "HISCUS groups its work into four public pillars. Every documented activity sits beneath one of them.",
    metaDescription: "The four programme pillars and documented projects delivered by HISCUS Nepal.",
    systemRoute: true,
    routePath: "/our-work",
    order: 11,
    sections: inOrder([
      { key: "our-work.pillars", type: "programmeCards", source: "The programmes collection." },
      {
        key: "our-work.approach",
        type: "featureCards",
        eyebrow: "How programmes are run",
        title: "Requested locally, planned jointly, and asked to leave something behind",
        items: [
          {
            title: "Requested locally",
            text: "Sessions and projects begin with a request from a ward, school, health post or community group, not with a plan brought in from outside.",
            order: 0,
          },
          {
            title: "Planned jointly",
            text: "Programmes are designed with the people who will host and maintain them, which is why HISCUS returns to the same communities across different kinds of work.",
            order: 1,
          },
          {
            title: "Durable by design",
            text: "Relief work is expected to leave infrastructure behind: a water tank, an access path, a protective wall.",
            order: 2,
          },
        ],
        settings: { tone: "ivory", columns: 3 },
        source: "HISCUS programme approach, as published on its programme pages.",
      },
      {
        key: "our-work.projects",
        type: "projectList",
        eyebrow: "Projects",
        title: "Individual projects",
        lead: "Each project page carries its own dates, locations and reported figures.",
        source: "The projects collection.",
      },
      {
        key: "our-work.cta",
        type: "cta",
        title: "Looking for a delivery partner in Nepal?",
        lead: "HISCUS works with foundations, INGOs, embassies, development agencies and companies on programmes in all four pillars.",
        ctas: [{ label: "Support our work", href: "/get-involved/support", order: 0 }],
        settings: { tone: "navy" },
        source: "HISCUS public participation routes.",
      },
    ]),
  },
  {
    slug: "stories",
    title: "Stories",
    headline: "Stories from the work",
    eyebrow: "From the field",
    lead:
      "Written only from documented events. Dates, places and figures come from HISCUS reporting; no participant is named or quoted beyond what has been published.",
    metaDescription: "Documented stories from HISCUS programmes and communities in Nepal.",
    systemRoute: true,
    routePath: "/stories",
    order: 12,
    sections: inOrder([{ key: "stories.list", type: "featureCards", source: "The stories collection." }]),
  },
  {
    slug: "transparency",
    title: "Transparency",
    headline: "Transparency",
    eyebrow: "Accountability",
    lead: "The organisation's legal identity, and the documents available to funders and supporters.",
    metaDescription: "HISCUS registration, governance, policies, reports and document availability.",
    systemRoute: true,
    routePath: "/transparency",
    order: 13,
    sections: inOrder([
      {
        key: "transparency.registration",
        type: "textBlock",
        eyebrow: "Legal identity",
        title: "Registration and affiliation",
        body: [
          {
            kind: "paragraph",
            text: "Every impact figure on this site carries the period it refers to. Figures whose reporting period is unclear are kept out of headline counters until they are confirmed.",
          },
          {
            kind: "paragraph",
            text: "Social media follower counts are not presented as impact, and no figure on this site has been extrapolated from a single event.",
          },
        ],
        texts: { panelTitle: "How we report figures" },
        ctas: [{ label: "See the impact record", href: "/impact", order: 0 }],
        source: "Organisation profile registrations and the site's reporting rules.",
      },
      {
        key: "transparency.documents",
        type: "documents",
        eyebrow: "Documents",
        title: "Reports, financials and policies",
        lead: "Grouped by what they are and the period they cover. Anything not published here is shared directly with funders and institutional partners.",
        ctas: [{ label: "Request documentation", href: "/get-involved/support", order: 0 }],
        settings: { tone: "ivory" },
        source: "The documents collection.",
      },
      { key: "transparency.partners", type: "partners", source: "The partners collection." },
    ]),
  },
  {
    slug: "where-we-work",
    title: "Where we work",
    headline: "Seven places, and what happened in each",
    eyebrow: "Geography",
    lead:
      "HISCUS works in a small number of communities repeatedly rather than in many communities once. These are the places its own reporting names.",
    metaDescription: "The communities and places named in HISCUS programme reporting across Nepal.",
    systemRoute: true,
    routePath: "/where-we-work",
    order: 14,
    sections: inOrder([
      {
        key: "where-we-work.intro",
        type: "imageText",
        title: "Depth before spread",
        body: [
          {
            kind: "paragraph",
            text: "In Bethanchowk, HISCUS supported the health post in January 2025, ran a women’s session in the same community four weeks later, and finished a water tank there that July. Three kinds of work, one community, nine months.",
          },
          {
            kind: "paragraph",
            text: "That is the pattern rather than the exception. Infrastructure that arrives once and leaves rarely survives its first repair; infrastructure planned alongside the health post and the women’s sessions belongs to people with a reason to maintain it.",
          },
        ],
        texts: {
          note: "HISCUS does not claim nationwide coverage. Where a completion year is not published, this page says so rather than estimating one.",
        },
        mediaKey: "rr-infrastructure",
        source: "client/src/data/projects.ts and client/src/data/locations.ts.",
      },
      {
        key: "where-we-work.places",
        type: "locations",
        eyebrow: "The record by place",
        title: "Documented locations",
        items: locations.map((place, order) => ({
          title: place.name,
          eyebrow: place.region,
          text: place.summary,
          label: place.pillars.join(" · "),
          facts: place.work.map((entry) => ({ label: entry.label, value: entry.detail, date: entry.year })),
          order,
        })),
        ctas: [{ label: "See the full impact record", href: "/impact", order: 0 }],
        settings: { tone: "ivory" },
        source: "client/src/data/locations.ts, transcribed from HISCUS project reporting.",
      },
      {
        key: "where-we-work.cta",
        type: "cta",
        title: "Work with us in one of these communities, or propose a new one.",
        lead: "Wards, schools, health posts and community groups can request a programme directly.",
        texts: { footnote: "Looking for a specific project?" },
        ctas: [
          { label: "Support our work", href: "/get-involved/support", order: 0 },
          { label: "Propose local collaboration", href: "/contact", order: 1 },
          { label: "Browse all programmes and projects", href: "/our-work", order: 2 },
        ],
        settings: { tone: "midnight" },
        source: "HISCUS public participation routes.",
      },
    ]),
  },
  ...programmePages,
];

/** Find the committed fallback for a system-page section by its stable key. */
export function findSeedSection(key: string): SeedSection | null {
  for (const page of seedPages) {
    const section = page.sections.find((candidate) => candidate.key === key);
    if (section) return section;
  }
  return null;
}

/** The committed page record and sections for one slug, or null. */
export function findSeedPage(slug: string): SeedPage | null {
  return seedPages.find((page) => page.slug === slug) ?? null;
}
